import { useQuery } from '@apollo/client'
import { FC, useMemo } from 'react'

import { GetVenueEventsQuery } from 'gql'
import { EVENT, EVENT_VIP } from 'routes/paths'
import { useScreenClass } from '~hooks'
import { ItemsList } from '~shared'
import { Button, GlowHeading } from '~ui'
import { getImageUrl, getServerTimeInApiFormat, time } from '~utils'
import { GET_VENUE_EVENTS } from 'gql/artwork/queries'

const ITEMS_TO_FETCH = 5

interface Variables {
  venueSlug: string
}

const Events: FC<Variables> = ({ venueSlug }) => {
  const variables = useMemo(
    () => ({
      venueSlug,
      first: ITEMS_TO_FETCH,
      eventStartDate_Gte: getServerTimeInApiFormat(true),
      announceDate_Lt: getServerTimeInApiFormat(true),
      orderBy: 'event_start_date_local',
      salesAs: 'buy_it_now_option',
      status: 'public',
    }),
    [venueSlug]
  )

  const { data, loading, fetchMore } = useQuery<GetVenueEventsQuery>(GET_VENUE_EVENTS, {
    variables,
    notifyOnNetworkStatusChange: true,
  })

  const { isMobile } = useScreenClass()

  const events = data?.allArtworks.edges.map(({ node }) => node) || []
  const pageInfo = data?.allArtworks.pageInfo

  const items = events?.map(
    ({
      artworkOrTourOrArtistImage,
      eventStartDateLocal,
      artistFullName,
      slug,
      tevoId,
      title,
      externalLink,
      externalVipLink,
      hasPackages,
    }) => ({
      fields: [
        ...(isMobile
          ? [
              [
                { text: artistFullName, bold: true },
                { text: title, gray: true },
                {
                  text: `${time.formatDate(eventStartDateLocal, 'dateOnly')}, ${time.formatDate(
                    eventStartDateLocal,
                    'dayAndTime'
                  )}`,
                  gray: true,
                },
              ],
            ]
          : [
              [
                {
                  text: time.formatDate(eventStartDateLocal, 'dateOnly'),
                  bold: true,
                },
                { text: time.formatDate(eventStartDateLocal, 'dayAndTime'), gray: true },
              ],
              [{ text: artistFullName, bold: true }, { text: title }],
            ]),
      ],
      imageSrc: getImageUrl(artworkOrTourOrArtistImage, 100, 100),
      external: !!externalLink || !!externalVipLink,
      link: externalVipLink || (hasPackages ? EVENT_VIP(slug) : externalLink || EVENT(slug)),
      actions: [
        // eslint-disable-next-line no-nested-ternary
        externalLink ? (
          <Button
            variant="primary"
            href={externalLink}
            key={externalLink}
            mode="external-link"
            rel="noopener noreferrer"
            target="_blank"
          >
            Tickets
          </Button>
        ) : tevoId ? (
          <Button variant="primary" href={EVENT(slug)} key={EVENT(slug)} mode="link">
            Tickets
          </Button>
        ) : null,
        // eslint-disable-next-line no-nested-ternary
        externalVipLink ? (
          <Button
            variant="primary"
            icon="star-filled"
            mode="external-link"
            href={externalVipLink}
            key={externalVipLink}
            rel="noopener noreferrer"
            target="_blank"
          >
            VIP
          </Button>
        ) : hasPackages ? (
          <Button
            variant="primary"
            icon="star-filled"
            mode="link"
            href={EVENT_VIP(slug)}
            key={EVENT_VIP(slug)}
          >
            VIP
          </Button>
        ) : null,
      ],
    })
  )

  const onLoadMore = () => {
    fetchMore({ variables: { after: pageInfo.endCursor } })
  }

  return (
    <>
      <GlowHeading Tag="h2" glow={false} fill>
        Events
      </GlowHeading>
      <ItemsList
        showLoadMoreButton={pageInfo?.hasNextPage}
        onLoadMore={onLoadMore}
        loading={loading}
        iconForMobile="tickets"
        items={items}
      />
    </>
  )
}

export default Events
